// React Query hooks for the WB product-groups surface (migration 169).
//
// Pairs with /api/v1/wb/product-groups/*. The list returns every GLOBAL group
// (platform-managed, company_id null) plus the tenant's own custom groups, each
// carrying an `is_excluded` flag (a global group the tenant has opted out of).
//
// Reads are open to weighbridge_operator (the kiosk picker needs the group
// list); writes are office-only on the server. Groups change rarely, so a 60s
// staleTime keeps the pickers + settings page snappy.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { queryKeys } from '../../../api/queryKeys';

// -- Types (mirror WbProductGroupDto on the server) -------------------------

export interface WbProductGroup {
  readonly enc_id: string;
  readonly company_id: number | null;
  readonly name: string;
  readonly code: string;
  readonly sort_order: number;
  readonly is_active: boolean;
  readonly is_global: boolean;
  readonly is_excluded: boolean;
  readonly created_at: string;
  readonly updated_at: string;
}

interface ListProductGroupsResponse {
  groups: WbProductGroup[];
}

export interface CreateProductGroupInput {
  name: string;
  code: string;
  sort_order?: number;
}

export interface UpdateProductGroupInput {
  name?: string;
  code?: string;
  sort_order?: number;
  is_active?: boolean;
}

// -- Queries ----------------------------------------------------------------

export function useWbProductGroups() {
  return useQuery<ListProductGroupsResponse>({
    queryKey: queryKeys.wb.productGroups,
    queryFn: async () => {
      const res = await apiClient.get<ListProductGroupsResponse>(
        endpoints.wb.productGroups,
      );
      return res.data;
    },
    staleTime: 15 * 60 * 1000, // Server caches for 30min; 15min client staleTime is safe
  });
}

// -- Mutations --------------------------------------------------------------

function useInvalidateGroups() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: queryKeys.wb.productGroups });
  };
}

export function useCreateProductGroup() {
  const invalidate = useInvalidateGroups();
  return useMutation<{ group: WbProductGroup }, Error, CreateProductGroupInput>({
    mutationFn: async (body) => {
      const res = await apiClient.post<
        { group: WbProductGroup },
        CreateProductGroupInput
      >(endpoints.wb.productGroups, body);
      return res.data;
    },
    onSuccess: invalidate,
  });
}

export function useUpdateProductGroup() {
  const invalidate = useInvalidateGroups();
  return useMutation<
    { group: WbProductGroup },
    Error,
    { encId: string; body: UpdateProductGroupInput }
  >({
    mutationFn: async ({ encId, body }) => {
      const res = await apiClient.patch<
        { group: WbProductGroup },
        UpdateProductGroupInput
      >(endpoints.wb.productGroup(encId), body);
      return res.data;
    },
    onSuccess: invalidate,
  });
}

export function useDeleteProductGroup() {
  const invalidate = useInvalidateGroups();
  return useMutation<{ deleted: true }, Error, string>({
    mutationFn: async (encId) => {
      const res = await apiClient.delete<{ deleted: true }>(
        endpoints.wb.productGroup(encId),
      );
      return res.data;
    },
    onSuccess: invalidate,
  });
}

/**
 * Toggle a tenant's opt-out of a GLOBAL group. `exclude=true` writes an
 * exclusion row (group hidden); `exclude=false` removes it (group visible).
 */
export function useToggleGlobalGroupExclusion() {
  const invalidate = useInvalidateGroups();
  return useMutation<
    { group: WbProductGroup },
    Error,
    { encId: string; exclude: boolean }
  >({
    mutationFn: async ({ encId, exclude }) => {
      const url = endpoints.wb.productGroupExclude(encId);
      const res = exclude
        ? await apiClient.post<{ group: WbProductGroup }, Record<string, never>>(url, {})
        : await apiClient.delete<{ group: WbProductGroup }>(url);
      return res.data;
    },
    onSuccess: invalidate,
  });
}
